import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { workflowAPI, customerAPI, carrierAPI, loadAPI } from '../api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  ListChecks, 
  Plus, 
  CheckCircle2, 
  Circle,
  Clock,
  Trash2,
  Package,
  Building2,
  Truck,
  ChevronDown,
  ChevronUp,
  Play,
  Filter
} from 'lucide-react';

const Workflows = () => {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [expandedWorkflow, setExpandedWorkflow] = useState(null);
  
  // Create workflow form state
  const [newWorkflow, setNewWorkflow] = useState({
    name: '',
    template_type: '',
    entity_type: '',
    entity_id: ''
  });
  const [entities, setEntities] = useState({ customers: [], carriers: [], loads: [] });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [workflowsRes, templatesRes] = await Promise.all([
        workflowAPI.getAll({ status: statusFilter !== 'all' ? statusFilter : undefined }),
        workflowAPI.getTemplates()
      ]);
      setWorkflows(workflowsRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error('Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchEntities = async () => {
    try {
      const [customersRes, carriersRes, loadsRes] = await Promise.all([
        customerAPI.getAll(),
        carrierAPI.getAll(),
        loadAPI.getAll()
      ]);
      setEntities({
        customers: customersRes.data,
        carriers: carriersRes.data,
        loads: loadsRes.data.slice(0, 50) // Limit to recent 50 loads
      });
    } catch (error) {
      console.error('Failed to fetch entities');
    }
  };

  const handleCreateWorkflow = async () => {
    if (!newWorkflow.template_type) {
      toast.error('Please select a workflow template');
      return;
    }
    
    try {
      await workflowAPI.create(newWorkflow);
      toast.success('Workflow created!');
      setShowCreateDialog(false);
      setNewWorkflow({ name: '', template_type: '', entity_type: '', entity_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create workflow');
    }
  };

  const handleStepToggle = async (workflowId, stepId, currentCompleted) => {
    try {
      const res = await workflowAPI.updateStep(workflowId, stepId, !currentCompleted);
      // Update local state
      setWorkflows(workflows.map(w => 
        w.id === workflowId ? res.data : w
      ));
      if (res.data.status === 'completed') {
        toast.success('Workflow completed!');
      }
    } catch (error) {
      toast.error('Failed to update step');
    }
  };

  const handleDeleteWorkflow = async (workflowId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this workflow?')) return;
    
    try {
      await workflowAPI.delete(workflowId);
      toast.success('Workflow deleted');
      setWorkflows(workflows.filter(w => w.id !== workflowId));
    } catch (error) {
      toast.error('Failed to delete workflow');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'cancelled': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
  };

  const getEntityIcon = (type) => {
    switch (type) {
      case 'customer': return <Building2 className="w-4 h-4" />;
      case 'carrier': return <Truck className="w-4 h-4" />;
      case 'load': return <Package className="w-4 h-4" />;
      default: return <ListChecks className="w-4 h-4" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <MainLayout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="workflows-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[var(--tms-text)]">Workflows</h1>
            <p className="text-sm text-[var(--tms-text-muted)] mt-1">Track step-by-step processes and tasks</p>
          </div>
          
          <Dialog open={showCreateDialog} onOpenChange={(open) => {
            setShowCreateDialog(open);
            if (open) fetchEntities();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="create-workflow-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Workflow
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create Workflow</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Workflow Template *</Label>
                  <Select 
                    value={newWorkflow.template_type} 
                    onValueChange={(v) => {
                      setNewWorkflow({
                        ...newWorkflow, 
                        template_type: v,
                        name: templates[v]?.name || ''
                      });
                    }}
                  >
                    <SelectTrigger data-testid="select-template">
                      <SelectValue placeholder="Select a template" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new_load">New Load Process</SelectItem>
                      <SelectItem value="carrier_onboarding">Carrier Onboarding</SelectItem>
                      <SelectItem value="customer_onboarding">Customer Onboarding</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Custom Name (Optional)</Label>
                  <Input
                    value={newWorkflow.name}
                    onChange={(e) => setNewWorkflow({...newWorkflow, name: e.target.value})}
                    placeholder={templates[newWorkflow.template_type]?.name || 'Workflow name'}
                    data-testid="input-workflow-name"
                  />
                </div>
                
                <div>
                  <Label>Link to Entity (Optional)</Label>
                  <Select 
                    value={newWorkflow.entity_type || 'none'} 
                    onValueChange={(v) => setNewWorkflow({...newWorkflow, entity_type: v === 'none' ? '' : v, entity_id: ''})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select entity type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="load">Load</SelectItem>
                      <SelectItem value="customer">Customer</SelectItem>
                      <SelectItem value="carrier">Carrier</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {newWorkflow.entity_type && (
                  <div>
                    <Label>Select {newWorkflow.entity_type}</Label>
                    <Select 
                      value={newWorkflow.entity_id} 
                      onValueChange={(v) => setNewWorkflow({...newWorkflow, entity_id: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={`Select ${newWorkflow.entity_type}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {newWorkflow.entity_type === 'customer' && entities.customers.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                        {newWorkflow.entity_type === 'carrier' && entities.carriers.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                        {newWorkflow.entity_type === 'load' && entities.loads.map(l => (
                          <SelectItem key={l.id} value={l.id}>{l.load_number || l.id.substring(0,8)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <Button onClick={handleCreateWorkflow} className="w-full" data-testid="submit-workflow">
                  <Play className="w-4 h-4 mr-2" />
                  Start Workflow
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-[var(--tms-text-muted)]" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Workflows List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12 text-[var(--tms-text-muted)]">
              Loading workflows...
            </div>
          ) : workflows.length === 0 ? (
            <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-xl p-8 text-center">
              <ListChecks className="w-12 h-12 mx-auto text-[var(--tms-text-muted)] mb-3" />
              <h3 className="text-lg font-medium text-[var(--tms-text)] mb-1">No workflows yet</h3>
              <p className="text-sm text-[var(--tms-text-muted)] mb-4">
                Create a workflow to track your processes step by step.
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Workflow
              </Button>
            </div>
          ) : (
            workflows.map((workflow) => (
              <div
                key={workflow.id}
                className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-xl overflow-hidden"
                data-testid={`workflow-${workflow.id}`}
              >
                {/* Workflow Header */}
                <div 
                  className="p-4 cursor-pointer hover:bg-[var(--tms-hover)] transition-colors"
                  onClick={() => setExpandedWorkflow(expandedWorkflow === workflow.id ? null : workflow.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[var(--tms-primary)]/10 flex items-center justify-center">
                        {getEntityIcon(workflow.entity_type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-[var(--tms-text)]">{workflow.name}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full capitalize border ${getStatusColor(workflow.status)}`}>
                            {workflow.status.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-[var(--tms-text-muted)] mt-1">
                          {workflow.entity_name && (
                            <span className="flex items-center gap-1">
                              {getEntityIcon(workflow.entity_type)}
                              {workflow.entity_name}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(workflow.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Progress */}
                      <div className="hidden sm:flex items-center gap-2">
                        <div className="w-24 h-2 bg-[var(--tms-hover)] rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[var(--tms-primary)] transition-all"
                            style={{ width: `${workflow.progress_pct}%` }}
                          />
                        </div>
                        <span className="text-sm text-[var(--tms-text-muted)] w-12">{workflow.progress_pct}%</span>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleDeleteWorkflow(workflow.id, e)}
                        className="text-[var(--tms-text-muted)] hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      
                      {expandedWorkflow === workflow.id ? (
                        <ChevronUp className="w-5 h-5 text-[var(--tms-text-muted)]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[var(--tms-text-muted)]" />
                      )}
                    </div>
                  </div>
                  
                  {/* Mobile Progress Bar */}
                  <div className="sm:hidden mt-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-[var(--tms-hover)] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[var(--tms-primary)] transition-all"
                          style={{ width: `${workflow.progress_pct}%` }}
                        />
                      </div>
                      <span className="text-xs text-[var(--tms-text-muted)]">{workflow.progress_pct}%</span>
                    </div>
                  </div>
                </div>
                
                {/* Expanded Steps */}
                {expandedWorkflow === workflow.id && (
                  <div className="border-t border-[var(--tms-border)] p-4 bg-[var(--tms-background)]">
                    <div className="space-y-2">
                      {workflow.steps.map((step, index) => (
                        <div 
                          key={step.id}
                          className={`flex items-start gap-3 p-3 rounded-lg ${
                            step.completed 
                              ? 'bg-green-500/10' 
                              : 'bg-[var(--tms-card)] hover:bg-[var(--tms-hover)]'
                          } transition-colors`}
                        >
                          <button
                            onClick={() => handleStepToggle(workflow.id, step.id, step.completed)}
                            className="mt-0.5 flex-shrink-0"
                            data-testid={`step-checkbox-${step.id}`}
                          >
                            {step.completed ? (
                              <CheckCircle2 className="w-5 h-5 text-green-500" />
                            ) : (
                              <Circle className="w-5 h-5 text-[var(--tms-text-muted)] hover:text-[var(--tms-primary)]" />
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-[var(--tms-text-muted)]">Step {index + 1}</span>
                              <h4 className={`font-medium ${step.completed ? 'text-[var(--tms-text-muted)] line-through' : 'text-[var(--tms-text)]'}`}>
                                {step.title}
                              </h4>
                            </div>
                            {step.description && (
                              <p className="text-sm text-[var(--tms-text-secondary)] mt-0.5">{step.description}</p>
                            )}
                            {step.completed && step.completed_by_name && (
                              <p className="text-xs text-[var(--tms-text-muted)] mt-1">
                                Completed by {step.completed_by_name} • {formatDate(step.completed_at)}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Workflows;
